import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Alert, Button, Card, Col, ConfigProvider, DatePicker, Descriptions, Empty, Row, Segmented, Space, Spin, Table, Tabs, Tag, Tooltip, Typography, message, theme } from "antd";
import { ClockCircleOutlined, ReloadOutlined } from "@ant-design/icons";
import * as echarts from "echarts";
import dayjs from "dayjs";
import businessDashboardApi from "../../api/v3/businessDashboardApi";
import { useAuth } from "../../hooks/useAuth";
import useBusinessDashboardData from "./hooks/useBusinessDashboardData";
import RouteNetworkMap from "./components/RouteNetworkMap";
import { ThresholdBar } from "./components/StatusVisuals";
import { DrillDownDrawer, useDrillDown } from "./components/DrillDownDrawer";
import { CustomerMatrix } from "./components/CustomerMatrix";
import {
    TONE_HEX,
    THRESHOLDS,
    rateTone,
    toneTagColor,
    riskColor,
    qualityColor,
} from "./utils/statusSemantics";
import "./BusinessDashboard.css";

const DASHBOARD_THEME = {
    algorithm: theme.defaultAlgorithm,
    token: {
        colorPrimary: "#2b6cff",
        colorInfo: "#2b6cff",
        colorSuccess: "#15a35a",
        colorWarning: "#d98a0b",
        colorError: "#e2483d",
        borderRadius: 12,
        colorBgContainer: "#ffffff",
        colorBgElevated: "#ffffff",
        colorBorderSecondary: "#e6ecf4",
        colorBorder: "#dbe3ee",
        colorText: "#1b2a41",
        colorTextSecondary: "#6a7b91",
        fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'PingFang SC', 'Microsoft YaHei', sans-serif",
    },
    components: {
        Card: { colorBgContainer: "#ffffff" },
        Table: {
            colorBgContainer: "transparent",
            headerBg: "#f3f6fb",
            rowHoverBg: "rgba(43,108,255,0.05)",
            borderColor: "#e6ecf4",
        },
        Tabs: { itemColor: "#6a7b91", itemSelectedColor: "#2b6cff", inkBarColor: "#2b6cff" },
        Segmented: { itemSelectedBg: "#ffffff", itemSelectedColor: "#1b2a41" },
        Drawer: { colorBgElevated: "#ffffff" },
    },
};

const { RangePicker } = DatePicker;
const { Paragraph, Text, Title } = Typography;

const PERMISSION_AMOUNT = "business_dashboard:management_amount_read";

const TAB_KEYS = {
    OVERVIEW: "overview",
    AIRCRAFT: "aircraft",
    ROUTES: "routes",
    CUSTOMERS: "customers",
    PARTNERS: "partners",
};

// 周期口径元数据。规划方案第五章（年度）与第六章（月度）是两套指标体系，
// 这里集中维护标签前缀和口径说明，供 KPI 卡片、模块标题和各 Tab 复用，
// 确保月度 / 年度切换时展示对应口径，而不是同一套布局两用。
const PERIOD_META = {
    monthly: {
        key: "monthly",
        label: "月度",
        prefix: "近 30 天",
        scopeNote: "滚动近 30 天",
        utilizationNote: "滚动近 30 天",
    },
    annual: {
        key: "annual",
        label: "年度",
        prefix: "年度",
        scopeNote: "年度口径",
        utilizationNote: "年度累计口径",
    },
};

const resolvePeriodMeta = (period) => PERIOD_META[period] || PERIOD_META.monthly;

// 月度采用「滚动近 30 天」窗口（今天往前 30 天 ~ 今天），避免月初自然月口径下
// 样本过少、完成率/装载率等指标失真；年度采用当年。详见规划方案第六章口径讨论。
const MONTHLY_ROLLING_DAYS = 30;

const monthlyRange = (now = dayjs()) => [now.subtract(MONTHLY_ROLLING_DAYS, "day"), now];

const annualRange = (now = dayjs()) => [now.startOf("year"), now.endOf("year")];

const defaultFilters = () => {
    const [start, end] = monthlyRange();
    return {
        start_date: start.format("YYYY-MM-DD"),
        end_date: end.format("YYYY-MM-DD"),
        period: "monthly",
        timezone: "Asia/Shanghai",
    };
};

const isNil = (value) => value === null || value === undefined;

const formatNumber = (value, digits = 0) => {
    if (isNil(value) || Number.isNaN(Number(value))) return "--";
    const safeDigits = Number.isInteger(digits) && digits >= 0 && digits <= 20 ? digits : 0;
    return Number(value).toLocaleString(undefined, {
        maximumFractionDigits: safeDigits,
        minimumFractionDigits: safeDigits,
    });
};

const formatHours = (value) => (isNil(value) ? "--" : `${formatNumber(value, 1)} h`);

const formatPercent = (value) => {
    if (isNil(value) || Number.isNaN(Number(value))) return "--";
    return `${formatNumber(Number(value) * 100, 1)}%`;
};

const formatDateTime = (value) => (value ? dayjs(value).format("MM-DD HH:mm") : "--");

const formatDate = (value) => (value ? dayjs(value).format("YYYY-MM-DD") : "--");

const formatMoney = (value) => {
    if (isNil(value) || Number.isNaN(Number(value))) return "--";
    return Number(value).toLocaleString(undefined, {
        maximumFractionDigits: 0,
        minimumFractionDigits: 0,
    });
};

const coverageNote = (bh, periodEnd) => {
    const end = bh?.planned_coverage_end;
    if (!end || !periodEnd || end >= periodEnd) return null;
    return `已发布计划覆盖至 ${end}，其后未排期不计入`;
};

const bhSourceNote = (bh) => {
    if (!bh) return null;
    const settlement = bh.settlement_leg_count;
    const block = bh.block_leg_count;
    const invoice = bh.invoice_leg_count;
    if (isNil(settlement) && isNil(block) && isNil(invoice)) return null;
    const parts = [];
    if (settlement) parts.push(`结算 ${formatNumber(settlement)} 段`);
    if (block) parts.push(`轮挡 ${formatNumber(block)} 段`);
    if (invoice) parts.push(`发票 ${formatNumber(invoice)} 段`);
    if (!block && !invoice && settlement) return "实际小时均来自结算";
    return parts.join(" · ") || null;
};

const salesUnbilled = (metrics) =>
    metrics?.billing_basis === "UNRECORDED" || (isNil(metrics?.billing_basis) && isNil(metrics?.receivable_amount) && isNil(metrics?.cash_received_amount));

const DATA_SOURCE_EMPTY_HINT = "请检查当前筛选周期，或确认对应业务数据源是否已覆盖。";

function DashboardEmpty({ description = "当前筛选条件下暂无数据", hint = DATA_SOURCE_EMPTY_HINT }) {
    return (
        <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
                <div className="business-dashboard-empty-text">
                    <Text>{description}</Text>
                    {hint ? <Text type="secondary">{hint}</Text> : null}
                </div>
            }
        />
    );
}

const tableEmptyText = (description, hint) => <DashboardEmpty description={description} hint={hint} />;

function PanelCard({ title, extra, children, className = "" }) {
    return (
        <Card className={`business-dashboard-card ${className}`} title={title} extra={extra} variant="outlined">
            {children}
        </Card>
    );
}

function BlockError({ error, onRetry }) {
    if (!error) return null;
    return (
        <Alert
            type="error"
            showIcon
            message="数据加载失败"
            description={error?.response?.data?.detail || error?.message || "请稍后重试"}
            action={
                onRetry ? (
                    <Button size="small" danger onClick={onRetry}>
                        重试
                    </Button>
                ) : null
            }
        />
    );
}

function PartialRequestAlert({ partialErrors, message = "部分数据加载失败" }) {
    if (!partialErrors?.length) return null;

    return (
        <Alert
            className="business-dashboard-partial-alert"
            type="warning"
            showIcon
            message={message}
            description={
                <div className="business-dashboard-quality-content">
                    <Text type="secondary">已展示成功返回的数据；点击重新加载会绕过 5 分钟缓存并重试失败接口。</Text>
                    <ul className="business-dashboard-warning-list">
                        {partialErrors.map((item) => (
                            <li key={item.key || item.label}>
                                <Tag color="gold">{item.label}</Tag>
                                <Text type="secondary">{item.message || "请求失败"}</Text>
                            </li>
                        ))}
                    </ul>
                </div>
            }
        />
    );
}

function RateTag({ value, green, amber }) {
    if (isNil(value) || Number.isNaN(Number(value))) return <span style={{ color: "#8a9bb0" }}>--</span>;
    const tone = rateTone(value, { green, amber });
    return <Tag color={toneTagColor(tone)} style={{ marginInlineEnd: 0 }}>{formatPercent(value)}</Tag>;
}

function MiniSparkline({ points, tone = "info" }) {
    const series = (points || []).map((p) => Number(p) || 0);
    if (series.length < 2) return null;
    const width = 132;
    const height = 34;
    const min = Math.min(...series);
    const max = Math.max(...series);
    const span = max - min || 1;
    const step = width / (series.length - 1);
    const coords = series.map((value, index) => [index * step, height - ((value - min) / span) * (height - 4) - 2]);
    const linePath = coords.map(([x, y], i) => `${i === 0 ? "M" : "L"} ${x.toFixed(1)} ${y.toFixed(1)}`).join(" ");
    const areaPath = `${linePath} L ${width} ${height} L 0 ${height} Z`;
    const stroke = TONE_HEX[tone] || TONE_HEX.info;
    const gradientId = `bd-spark-${tone}`;
    return (
        <svg className="business-dashboard-spark" viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none" aria-hidden="true">
            <defs>
                <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={stroke} stopOpacity="0.32" />
                    <stop offset="100%" stopColor={stroke} stopOpacity="0" />
                </linearGradient>
            </defs>
            <path d={areaPath} fill={`url(#${gradientId})`} />
            <path d={linePath} fill="none" stroke={stroke} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    );
}

// 完成率小圆环：单个比率用环形比线性条更直观，且用内联 SVG 画，避免为单个指标引入图表库。
function MiniDonut({ value, tone, size = 44, stroke = 5 }) {
    const pct = Math.max(0, Math.min(100, Number(value) || 0));
    const radius = (size - stroke) / 2;
    const circumference = 2 * Math.PI * radius;
    const center = size / 2;
    return (
        <svg className="business-dashboard-kpi-donut" width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden="true">
            <circle cx={center} cy={center} r={radius} fill="none" stroke="rgba(20, 40, 70, 0.08)" strokeWidth={stroke} />
            <circle
                cx={center}
                cy={center}
                r={radius}
                fill="none"
                stroke={TONE_HEX[tone]}
                strokeWidth={stroke}
                strokeLinecap="round"
                strokeDasharray={circumference}
                strokeDashoffset={circumference * (1 - pct / 100)}
                transform={`rotate(-90 ${center} ${center})`}
            />
        </svg>
    );
}

function KpiCard({ item, loading }) {
    const tone = item.tone || "info";
    if (loading) {
        return <div className="business-dashboard-kpi-card business-dashboard-kpi-card--loading" />;
    }
    const hasProgress = item.progress !== undefined && item.progress !== null;
    return (
        <div className={`business-dashboard-kpi-card business-dashboard-kpi-card--${tone}`}>
            <span className="business-dashboard-kpi-bar" />
            <div className="business-dashboard-kpi-head">
                <span className="business-dashboard-kpi-title">{item.title}</span>
            </div>
            <div className="business-dashboard-kpi-value">
                {item.display}
                {item.suffix ? <span className="business-dashboard-kpi-suffix">{item.suffix}</span> : null}
            </div>
            <div className="business-dashboard-kpi-foot">
                <div className="business-dashboard-kpi-foot-row">
                    <span className="business-dashboard-kpi-note">{item.note}</span>
                    {hasProgress ? <MiniDonut value={item.progress} tone={tone} /> : null}
                </div>
                {item.spark ? <MiniSparkline points={item.spark} tone={tone} /> : null}
            </div>
        </div>
    );
}

function KpiStrip({ overview, bhSummary, bhTimeSeries, loading, periodMeta }) {
    const metrics = overview?.metrics || {};
    const bh = metrics.bh || bhSummary?.metrics || {};
    const flights = metrics.flights || {};
    const hasCommercialFlightSample = !isNil(flights.actual_commercial_flights) || !isNil(flights.planned_commercial_flights);

    const series = bhTimeSeries?.items || [];
    const plannedSpark = series.map((item) => item.planned_bh || 0);
    const actualSpark = series.map((item) => item.actual_bh || 0);
    const prefix = periodMeta.prefix;
    const publishedCoverage = coverageNote(bh, overview?.period?.end_date);
    const sourceNote = bhSourceNote(bh);

    const plannedCommercialCard = {
        key: "planned-commercial",
        title: `${prefix}计划航段`,
        display: isNil(flights.planned_commercial_flights) ? "--" : formatNumber(flights.planned_commercial_flights),
        suffix: isNil(flights.planned_commercial_flights) ? undefined : "段",
        tone: "info",
        note: publishedCoverage || "首发基准航段（后续改版不计），与计划 BH 同口径",
    };
    const actualCommercialCard = {
        key: "actual-commercial",
        title: `${prefix}实际航段`,
        display: isNil(flights.actual_commercial_flights) ? "--" : formatNumber(flights.actual_commercial_flights),
        suffix: isNil(flights.actual_commercial_flights) ? undefined : "段",
        tone: "info",
        note: hasCommercialFlightSample ? `计划 ${formatNumber(flights.planned_commercial_flights)} 段` : "已完成航段",
    };

    const plannedBhCard = {
        key: "planned-bh",
        title: `${prefix}计划 BH`,
        display: formatNumber(bh.planned_bh, 1),
        suffix: "h",
        tone: "info",
        note: publishedCoverage || periodMeta.scopeNote,
        spark: plannedSpark,
    };
    const actualBhCard = {
        key: "actual-bh",
        title: `${prefix}实际 BH`,
        display: formatNumber(bh.actual_bh, 1),
        suffix: "h",
        tone: "info",
        note: sourceNote || periodMeta.utilizationNote,
        spark: actualSpark,
    };
    const bhRateCard = {
        key: "bh-rate",
        title: `${prefix} BH 完成率`,
        display: formatPercent(bh.completion_rate),
        tone: rateTone(bh.completion_rate, THRESHOLDS.bhCompletion),
        note: `实际航段 ${formatNumber(bhSummary?.metrics?.actual_leg_count ?? flights.actual_legs)} 个`,
        progress: isNil(bh.completion_rate) ? null : Number(bh.completion_rate) * 100,
    };
    const flightRateCard = {
        key: "flight-rate",
        title: `${prefix}航段完成率`,
        display: formatPercent(flights.completion_rate),
        tone: rateTone(flights.completion_rate, THRESHOLDS.flightCompletion),
        note: hasCommercialFlightSample ? `${formatNumber(flights.actual_commercial_flights)} / ${formatNumber(flights.planned_commercial_flights)}` : "暂无航段样本",
        progress: isNil(flights.completion_rate) ? null : Number(flights.completion_rate) * 100,
    };

    // 月度 / 年度统一同一套 KPI：按「计划 → 实际 → 率」分组——先航段三件套，再 BH 三件套。
    // 卡片标题里的周期前缀（近 30 天 / 年度）由 prefix 自动适配。
    const cards = [plannedCommercialCard, actualCommercialCard, flightRateCard, plannedBhCard, actualBhCard, bhRateCard];

    return (
        <Row gutter={[16, 16]} className="business-dashboard-kpis">
            {cards.map((item, index) => (
                <Col flex="1 1 200px" key={item.key}>
                    <div className="business-dashboard-kpi-anim" style={{ animationDelay: `${index * 60}ms` }}>
                        <KpiCard item={item} loading={loading} />
                    </div>
                </Col>
            ))}
        </Row>
    );
}

// 图表渲染内核：只负责 echarts 生命周期（init/resize/dispose），不含卡片与空态，
// 便于在自定义面板里复用（如航线集中度面板要在同一张卡片内并排指标条与图表）。
function EChart({ data, optionFactory, height = 300 }) {
    const chartRef = useRef(null);
    const chartInstanceRef = useRef(null);

    useEffect(() => {
        if (!chartRef.current) return undefined;
        let disposed = false;
        const renderChart = () => {
            if (disposed || !chartRef.current) return;
            if (!chartRef.current.clientWidth || !chartRef.current.clientHeight) return;
            if (!chartInstanceRef.current) {
                chartInstanceRef.current = echarts.init(chartRef.current);
            }
            chartInstanceRef.current.setOption(optionFactory(data), true);
            chartInstanceRef.current.resize();
        };

        const frameId = window.requestAnimationFrame(renderChart);
        const resizeObserver = typeof ResizeObserver !== "undefined" ? new ResizeObserver(renderChart) : null;
        resizeObserver?.observe(chartRef.current);
        const resize = () => renderChart();
        window.addEventListener("resize", resize);
        return () => {
            disposed = true;
            window.cancelAnimationFrame(frameId);
            resizeObserver?.disconnect();
            window.removeEventListener("resize", resize);
        };
    }, [data, optionFactory]);

    useEffect(
        () => () => {
            chartInstanceRef.current?.dispose();
            chartInstanceRef.current = null;
        },
        [],
    );

    return <div ref={chartRef} style={{ width: "100%", height }} />;
}

function EChartPanel({ title, data, optionFactory, height = 300, emptyText = "当前筛选条件下暂无数据", emptyHint = DATA_SOURCE_EMPTY_HINT }) {
    const hasData = Array.isArray(data) ? data.length > 0 : Boolean(data);
    return (
        <PanelCard title={title}>
            {hasData ? <EChart data={data} optionFactory={optionFactory} height={height} /> : <DashboardEmpty description={emptyText} hint={emptyHint} />}
        </PanelCard>
    );
}

// 预警体系按规划方案第七章重构：以「指标」为主维度（7.1），每个指标给一个公司级红黄绿；
// 数据源未接入的指标标「待接入」，已接入但覆盖不足的标「数据待补齐」；具体对象（航线/飞机
// 等，7.2）作为该指标下的二级下钻。
const RISK_TONE_ORDER = { RED: 0, YELLOW: 1, GREEN: 2 };

const OBJECT_TYPE_LABELS = { ROUTE: "航线", AIRCRAFT: "飞机", CUSTOMER: "客户", AIRLINE: "航司", AIRPORT: "机场", CHARTER: "包机" };

function indicatorToneMeta(indicator) {
    if (indicator.status === "PENDING") return { color: "default", label: "待接入" };
    if (indicator.status === "INSUFFICIENT_DATA") return { color: "default", label: "数据待补齐" };
    const tone = String(indicator.tone || "").toUpperCase();
    if (tone === "RED") return { color: "red", label: "红" };
    if (tone === "YELLOW") return { color: "gold", label: "黄" };
    if (tone === "GREEN") return { color: "green", label: "绿" };
    return { color: "default", label: "--" };
}

function formatThresholdValueByUnit(unit, value) {
    if (isNil(value)) return "--";
    if (unit === "rate") return formatPercent(value);
    if (unit === "hours") return `${formatNumber(value, 0)}h`;
    return formatNumber(value);
}

function formatIndicatorValue(indicator) {
    if (isNil(indicator.value)) return "--";
    if (indicator.unit === "rate") return formatPercent(indicator.value);
    if (indicator.unit === "hours") return `${formatNumber(indicator.value, 1)}h`;
    return formatNumber(indicator.value);
}

function formatThresholdRef(indicator) {
    const green = formatThresholdValueByUnit(indicator.unit, indicator.thresholds?.green);
    const amber = formatThresholdValueByUnit(indicator.unit, indicator.thresholds?.amber);
    return indicator.direction === "higher" ? `绿≥${green} · 黄≥${amber}` : `绿≤${green} · 黄≤${amber}`;
}

function RiskBreakdownTable({ indicator }) {
    const rows = (indicator.breakdown || []).map((item, index) => ({ key: `${item.object_type}-${item.object_id}-${index}`, ...item }));
    if (!rows.length) {
        return <DashboardEmpty description="该指标暂无可下钻对象" hint="当前周期内该指标没有对象级明细。" />;
    }
    const hasBh = rows.some((row) => !isNil(row.planned_bh) || !isNil(row.actual_bh));
    const hasLegCounts = rows.some((row) => !isNil(row.planned_leg_count) || !isNil(row.actual_leg_count));
    const columns = [
        { title: "对象", dataIndex: "object_id", key: "object", render: (value, row) => `${OBJECT_TYPE_LABELS[row.object_type] || row.object_type} · ${value}` },
        { title: "数值", dataIndex: "value", key: "value", align: "right", render: (value) => formatThresholdValueByUnit(indicator.unit, value) },
    ];
    if (hasBh) {
        columns.push(
            { title: "计划BH", dataIndex: "planned_bh", key: "planned_bh", align: "right", render: (value) => formatNumber(value, 1) },
            { title: "实际BH", dataIndex: "actual_bh", key: "actual_bh", align: "right", render: (value) => formatNumber(value, 1) },
        );
    }
    if (hasLegCounts) {
        columns.push(
            { title: "计划航段", dataIndex: "planned_leg_count", key: "planned_leg_count", align: "right", render: formatNumber },
            { title: "实际航段", dataIndex: "actual_leg_count", key: "actual_leg_count", align: "right", render: formatNumber },
        );
    }
    columns.push({ title: "状态", dataIndex: "tone", key: "tone", render: (tone) => { const meta = indicatorToneMeta({ status: "OK", tone }); return <Tag color={meta.color}>{meta.label}</Tag>; } });
    return <Table size="small" pagination={false} columns={columns} dataSource={rows} />;
}

function RiskIndicatorPanel({ risks }) {
    const { selected, isOpen, open, close } = useDrillDown();
    const [showPending, setShowPending] = useState(false);
    const indicators = risks?.indicators || [];
    const summary = risks?.summary || {};
    if (!indicators.length) {
        return <DashboardEmpty description="当前筛选条件下暂无预警指标" hint="预警指标体系尚未返回数据。" />;
    }
    const evaluated = indicators.filter((item) => item.status !== "PENDING");
    evaluated.sort((a, b) => (RISK_TONE_ORDER[a.tone] ?? 3) - (RISK_TONE_ORDER[b.tone] ?? 3));
    const pending = indicators.filter((item) => item.status === "PENDING");
    const redCount = summary.red_count ?? evaluated.filter((item) => item.tone === "RED").length;
    const yellowCount = summary.yellow_count ?? evaluated.filter((item) => item.tone === "YELLOW").length;
    const greenCount = summary.green_count ?? evaluated.filter((item) => item.tone === "GREEN").length;
    const pendingCount = summary.pending_count ?? pending.length;

    const renderRow = (indicator) => {
        const meta = indicatorToneMeta(indicator);
        const canDrill = (indicator.breakdown || []).length > 0;
        return (
            <div
                className={`business-dashboard-risk-item${canDrill ? " is-drillable" : ""}`}
                key={indicator.indicator_code}
                onClick={canDrill ? () => open(indicator) : undefined}
                role={canDrill ? "button" : undefined}
                tabIndex={canDrill ? 0 : undefined}
            >
                <Tag color={meta.color}>{meta.label}</Tag>
                <div className="business-dashboard-risk-body">
                    <div className="business-dashboard-risk-head">
                        <Text strong className="business-dashboard-risk-title" title={indicator.name}>
                            {indicator.name}
                        </Text>
                        <Text strong>{formatIndicatorValue(indicator)}</Text>
                    </div>
                    <div>
                        <Text type="secondary">
                            {formatThresholdRef(indicator)}
                            {canDrill ? ` · ${indicator.breakdown.length} 个对象，点击下钻` : ` · 来源 ${indicator.source}`}
                        </Text>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <>
            <div className="business-dashboard-side-summary">
                <Tag color="red">红 {formatNumber(redCount)}</Tag>
                <Tag color="gold">黄 {formatNumber(yellowCount)}</Tag>
                <Tag color="green">绿 {formatNumber(greenCount)}</Tag>
                <Tag color="default">待接入 {formatNumber(pendingCount)}</Tag>
            </div>
            <div className="business-dashboard-risk-list">
                {evaluated.length ? evaluated.map(renderRow) : <Text type="secondary">已接入指标暂无可判定结果。</Text>}
                {pending.length ? (
                    <div className="business-dashboard-risk-pending">
                        <Button type="link" size="small" className="business-dashboard-risk-pending-toggle" onClick={() => setShowPending((value) => !value)}>
                            {showPending ? "收起" : "展开"}待接入指标（{pending.length}）
                        </Button>
                        {showPending ? pending.map(renderRow) : null}
                    </div>
                ) : null}
            </div>
            <DrillDownDrawer open={isOpen} title={selected ? `${selected.name} · 对象下钻` : ""} onClose={close} width={640}>
                {selected ? <RiskBreakdownTable indicator={selected} /> : null}
            </DrillDownDrawer>
        </>
    );
}

const CHART_AXIS_COLOR = "#6a7b91";
const CHART_SPLIT_COLOR = "rgba(20,40,70,0.08)";
const CHART_TOOLTIP = {
    backgroundColor: "#ffffff",
    borderColor: "#dbe3ee",
    textStyle: { color: "#1b2a41" },
    extraCssText: "box-shadow:0 6px 18px rgba(16,32,64,0.12);",
};

const bhTrendOption = (items) => ({
    tooltip: { trigger: "axis", ...CHART_TOOLTIP },
    legend: { top: 0, textStyle: { color: "#6a7b91" } },
    grid: { left: 48, right: 24, top: 44, bottom: 36 },
    xAxis: {
        type: "category",
        data: items.map((item) => item.period),
        axisLabel: { color: CHART_AXIS_COLOR },
        axisLine: { lineStyle: { color: CHART_SPLIT_COLOR } },
    },
    yAxis: {
        type: "value",
        axisLabel: { formatter: "{value}h", color: CHART_AXIS_COLOR },
        splitLine: { lineStyle: { color: CHART_SPLIT_COLOR, type: "dashed" } },
    },
    series: [
        {
            name: "计划 BH",
            type: "bar",
            data: items.map((item) => item.planned_bh || 0),
            itemStyle: { color: "rgba(43,108,255,0.28)", borderRadius: [3, 3, 0, 0] },
        },
        {
            name: "实际 BH",
            type: "line",
            smooth: true,
            data: items.map((item) => item.actual_bh || 0),
            symbolSize: 6,
            lineStyle: { color: "#2b6cff", width: 3 },
            itemStyle: { color: "#2b6cff" },
            areaStyle: { color: "rgba(43,108,255,0.10)" },
        },
    ],
});

const CONCENTRATION_TOP_ROUTES = 15;

// 航线集中度：横向条形按实际 BH 从高到低排名（航线一多时横向比纵向更耐扩展、标签更易读，
// 不必旋转）。不画累计占比；「是否过度依赖少数航线」由上方「最大航线占比 / 等效航线数」概括。
const routeConcentrationOption = (rows) => ({
    tooltip: {
        trigger: "axis",
        axisPointer: { type: "shadow" },
        ...CHART_TOOLTIP,
        formatter: (params) => {
            const idx = params?.[0]?.dataIndex ?? 0;
            const row = rows[idx];
            if (!row) return "";
            return `${row.label}<br/>实际 BH ${formatNumber(row.bh, 1)} h · 占比 ${formatPercent(row.share)}`;
        },
    },
    grid: { left: 8, right: 56, top: 8, bottom: 8, containLabel: true },
    xAxis: {
        type: "value",
        axisLabel: { formatter: "{value}h", color: CHART_AXIS_COLOR },
        splitLine: { lineStyle: { color: CHART_SPLIT_COLOR, type: "dashed" } },
    },
    yAxis: {
        type: "category",
        inverse: true,
        data: rows.map((row) => row.label),
        axisLabel: { color: CHART_AXIS_COLOR },
        axisLine: { lineStyle: { color: CHART_SPLIT_COLOR } },
        axisTick: { show: false },
    },
    series: [
        {
            name: "实际 BH",
            type: "bar",
            data: rows.map((row) => Number(row.bh.toFixed(1))),
            barMaxWidth: 18,
            itemStyle: { color: "#2b6cff", borderRadius: [0, 3, 3, 0] },
            label: { show: true, position: "right", color: CHART_AXIS_COLOR, formatter: (params) => `${formatNumber(params.value, 1)}h` },
        },
    ],
});

function OverviewTab({ sharedData, loading, deferredLoading, error, deferredError, onRetry, hasAmountPermission, periodMeta }) {
    const prefix = periodMeta.prefix;

    const fleetUtilGroups = useMemo(() => {
        const metrics = sharedData.overview?.metrics || {};
        const fleet = metrics.fleet || {};
        const bh = metrics.bh || sharedData.bhSummary?.metrics || {};
        const waiting = { tag: "待接入", value: "--" };
        return [
            {
                title: "机队可用率与利用率",
                items: [
                    { label: "管理飞机总数", value: isNil(fleet.managed_aircraft_count) ? "--" : `${formatNumber(fleet.managed_aircraft_count)} 架` },
                    { label: "运营中飞机", value: isNil(fleet.active_aircraft_count) ? "--" : `${formatNumber(fleet.active_aircraft_count)} 架`, sub: "主数据状态为运营中" },
                    { label: "本窗口在飞", value: isNil(bh.flying_aircraft_count) ? "--" : `${formatNumber(bh.flying_aircraft_count)} 架`, sub: "有实际航段的飞机" },
                    {
                        label: "飞机可用率",
                        value: fleet.availability_basis === "UNRECORDED" || isNil(fleet.availability_rate) ? "--" : formatPercent(fleet.availability_rate),
                        ...(fleet.availability_basis === "UNRECORDED"
                            ? { tag: "未记录", tagColor: "default", sub: "本窗口没有不可用记录，不能当成全程可用" }
                            : isNil(fleet.availability_rate)
                              ? { tag: "待接入" }
                              : { sub: "只统计有不可用记录的飞机" }),
                    },
                    {
                        label: "机队平均日利用率",
                        value: isNil(bh.avg_daily_utilization) ? "--" : `${formatHours(bh.avg_daily_utilization)}/天`,
                        ...(isNil(bh.avg_daily_utilization) ? { tag: "待接入" } : { sub: isNil(bh.flying_aircraft_count) ? undefined : `按在飞 ${formatNumber(bh.flying_aircraft_count)} 架分摊` }),
                    },
                    { label: "单机航班量（预计/可售/实际）", ...waiting },
                    { label: "单机实际 BH ÷ 可运营天数", ...waiting },
                ],
            },
        ];
    }, [sharedData.overview, sharedData.bhSummary]);

    const bhChart = loading ? (
        <Card>
            <Spin />
        </Card>
    ) : (
        <EChartPanel title={`${prefix}计划 vs 实际 BH`} data={sharedData.bhTimeSeries?.items || []} optionFactory={bhTrendOption} height={320} />
    );

    return (
        <div className="business-dashboard-tab-grid">
            <div className="business-dashboard-main-column">
                <BlockError error={error} onRetry={onRetry} />
                <PartialRequestAlert partialErrors={deferredError?.partialErrors} message="部分概览扩展数据加载失败" />
                {bhChart}
                <PanelCard title="机队可用率与利用率" extra={<Text type="secondary">单机三口径航班量缺数据源 · 占位</Text>}>
                    <MetricSlotGrid groups={fleetUtilGroups} />
                </PanelCard>
                <PanelCard title="航线网络态势" extra={<Tag color="cyan">Network</Tag>} className="business-dashboard-map-card">
                    <Spin spinning={deferredLoading && !sharedData.routesPerformance}>
                        <RouteNetworkMap routes={sharedData.routesPerformance?.items || []} />
                    </Spin>
                </PanelCard>
                <PanelCard title={`${prefix}装载率`} extra={<Text type="secondary">只统计有报文的航段</Text>}>
                    <Spin spinning={deferredLoading && !sharedData.cargoSummary}>
                        <CargoSummaryPanel cargoSummary={sharedData.cargoSummary} />
                    </Spin>
                </PanelCard>
                {hasAmountPermission && (
                    <PanelCard title={`${prefix}销售与燃油附加费差异`} extra={<Tag color="blue">金额权限</Tag>}>
                        <Spin spinning={deferredLoading && !sharedData.salesSummary}>
                            <SalesSummaryPanel salesSummary={sharedData.salesSummary} />
                        </Spin>
                    </PanelCard>
                )}
            </div>
            <div className="business-dashboard-side-column">
                <PanelCard title="红黄绿预警（按指标）">
                    <Spin spinning={deferredLoading && !sharedData.risks}>
                        <RiskIndicatorPanel risks={sharedData.risks} />
                    </Spin>
                </PanelCard>
                <PanelCard title="未来运力锁定" extra={<Text type="secondary">缺可用率数据 · 待接入</Text>}>
                    <CapacityOutlookPanel />
                </PanelCard>
            </div>
        </div>
    );
}

function CargoSummaryPanel({ cargoSummary }) {
    const metrics = cargoSummary?.metrics;
    const cargoLegs = metrics?.cargo_leg_count;
    const actualLegs = metrics?.actual_leg_count;
    const ratedLegs = metrics?.weight_rated_leg_count;
    const hasCargo = Number(cargoLegs) > 0;
    const coverage = hasCargo && Number(actualLegs) > 0 ? `${formatNumber(cargoLegs)} / ${formatNumber(actualLegs)} 段` : "--";
    const weightValue = isNil(metrics?.weight_load_factor) ? "--" : formatPercent(metrics.weight_load_factor);
    const uldValue = isNil(metrics?.uld_utilization_rate) ? "--" : formatPercent(metrics.uld_utilization_rate);

    return (
        <MetricSlotGrid
            groups={[
                {
                    title: "有报文的航段",
                    items: [
                        {
                            label: "报文覆盖",
                            value: coverage,
                            ...(hasCargo ? { sub: "有货载报文的完成航段 / 实际航段" } : { tag: "待接入", sub: "本窗口没有货载报文" }),
                        },
                        {
                            label: "重量载运率",
                            value: weightValue,
                            ...(isNil(metrics?.weight_load_factor)
                                ? { tag: hasCargo ? "缺货运能力" : "待接入", sub: hasCargo ? "有报文的飞机都没填货运能力" : "缺飞机货运能力" }
                                : { sub: `只含有货运能力的 ${formatNumber(ratedLegs)} 段` }),
                        },
                        {
                            label: "板位利用率",
                            value: uldValue,
                            ...(isNil(metrics?.uld_utilization_rate)
                                ? { tag: "待接入", sub: "缺报文可用板位" }
                                : { sub: "有舱位报文的航段，已装板位 / 全部板位" }),
                        },
                        { label: "体积利用率", tag: "待接入", value: "--", sub: "缺有效可用装载体积" },
                    ],
                },
            ]}
        />
    );
}

function CapacityOutlookPanel() {
    // 运力锁定率的分母「可销售BH」= 可运营天数 × 目标日利用率，其中可运营天数需扣减
    // 维修/AOG/停场等不可用时间。这些数据源尚未接入，缺失时只能按「飞机全程可用」乐观兜底，
    // 算出来的是假设上限而非真实可销售运力，会误导判断，故整组按规划口径标「待接入」。
    return (
        <MetricSlotGrid
            groups={[
                {
                    title: "运力锁定（规划口径）",
                    items: [
                        { label: "下月运力锁定率", tag: "待接入", value: "--", sub: "需可用率/维修/AOG 数据" },
                        { label: "未来 3 个月运力锁定率", tag: "待接入", value: "--" },
                        { label: "未来 6 个月运力锁定率", tag: "待接入", value: "--" },
                        { label: "未来 12 个月运力锁定率", tag: "待接入", value: "--" },
                        { label: "年度运力锁定率", tag: "待接入", value: "--" },
                        { label: "未售可用 BH", tag: "待接入", value: "--" },
                    ],
                },
            ]}
        />
    );
}

// 首页只展示规划方案「销售 8 项」核心，其余明细（到账率/应收/开票/成本/油价/议价/合同风险）下沉到「客户履约」Tab。
function SalesSummaryPanel({ salesSummary }) {
    const metrics = salesSummary?.metrics;
    if (!metrics) {
        return <DashboardEmpty description="当前筛选条件下暂无销售经营数据" />;
    }

    return (
        <MetricSlotGrid
            groups={[
                {
                    title: "合同与收入",
                    items: [
                        { label: "有效合同数", value: isNil(metrics.active_contract_count) ? "--" : `${formatNumber(metrics.active_contract_count)} 个` },
                        { label: "合同预计航班量", tag: "待接入", value: "--" },
                        { label: "合同实际航班量", tag: "待接入", value: "--" },
                        { label: "预计收入", value: metrics.expected_revenue === null ? "--" : formatMoney(metrics.expected_revenue), ...(metrics.expected_revenue === null ? { tag: "口径待治理" } : {}) },
                        salesUnbilled(metrics)
                            ? { label: "实际收入（到账）", value: "--", tag: "尚未入账", tagColor: "gold", sub: "本窗口没有应收记录" }
                            : { label: "实际收入（到账）", value: isNil(metrics.cash_received_amount) ? "--" : formatMoney(metrics.cash_received_amount) },
                    ],
                },
                {
                    title: "燃油附加费 · 成本",
                    items: [
                        { label: "燃油附加费收取率", tag: "待接入", value: "--" },
                        { label: "燃油附加费差异", value: metrics.fuel_surcharge_difference === null ? "--" : formatMoney(metrics.fuel_surcharge_difference), ...(metrics.fuel_surcharge_difference === null ? { tag: "口径待治理" } : {}) },
                        { label: "成本测算偏差率", tag: "待接入", value: "--" },
                    ],
                },
            ]}
        />
    );
}

// 销售明细：首页「销售 8 项」之外的扩展项，归入「客户履约」Tab。
function SalesDetailPanel({ salesSummary }) {
    const metrics = salesSummary?.metrics;
    if (!metrics) {
        return <DashboardEmpty description="当前筛选条件下暂无销售经营数据" />;
    }

    const unbilled = salesUnbilled(metrics);

    return (
        <div className="business-dashboard-stack-panel">
            {unbilled ? (
                <Text type="secondary">本窗口没有应收记录，到账、应收和开票记为尚未入账。</Text>
            ) : (
                <div className="business-dashboard-threshold-list">
                    <ThresholdBar
                        label="到账率"
                        value={metrics.cash_received_rate}
                        thresholds={THRESHOLDS.cashReceivedRate}
                        target={THRESHOLDS.cashReceivedRate.green}
                        subText={`到账 ${formatMoney(metrics.cash_received_amount)} · 应收 ${formatMoney(metrics.receivable_amount)}`}
                    />
                </div>
            )}
            <Descriptions column={2} size="small">
                <Descriptions.Item label="合同数">{formatNumber(metrics.contract_count)}</Descriptions.Item>
                <Descriptions.Item label="应收">{unbilled ? "尚未入账" : formatMoney(metrics.receivable_amount)}</Descriptions.Item>
                <Descriptions.Item label="开票">{unbilled ? "尚未入账" : formatMoney(metrics.invoiced_amount)}</Descriptions.Item>
            </Descriptions>
            <MetricSlotGrid
                groups={[
                    {
                        title: "成本与价格",
                        items: [
                            { label: "测算成本（按成本口径分类）", tag: "待接入", value: "--" },
                            { label: "航油价格标准", tag: "待接入", value: "--" },
                            { label: "签约价格", tag: "待接入", value: "--" },
                            { label: "议价次数 / 减免金额", tag: "待接入", value: "--" },
                        ],
                    },
                    {
                        title: "合同执行与风险",
                        items: [
                            { label: "合同执行率", tag: "待接入", value: "--" },
                            { label: "单航班实际收入低于预计（班数）", tag: "待接入", value: "--" },
                            { label: "价格倒挂 / 合同执行风险", tag: "待接入", value: "--" },
                        ],
                    },
                ]}
            />
        </div>
    );
}

const buildDistribution = (items, keyFn, fallback = "未分类") => {
    const counts = new Map();
    (items || []).forEach((item) => {
        const raw = keyFn(item);
        const key = isNil(raw) || raw === "" ? fallback : String(raw);
        counts.set(key, (counts.get(key) || 0) + 1);
    });
    const total = items?.length || 0;
    return Array.from(counts.entries())
        .map(([label, count]) => ({ label, count, ratio: total ? count / total : 0 }))
        .sort((a, b) => b.count - a.count);
};

const sumBy = (rows, key) => (rows || []).reduce((sum, row) => sum + (Number(row[key]) || 0), 0);

const avgBy = (rows, key) => {
    const values = (rows || []).map((row) => Number(row[key])).filter((value) => !Number.isNaN(value));
    return values.length ? values.reduce((a, b) => a + b, 0) / values.length : null;
};

function MetricSlotGrid({ groups }) {
    return (
        <div className="business-dashboard-metric-groups">
            {groups.map((group) => (
                <div className="business-dashboard-metric-group" key={group.title}>
                    <div className="business-dashboard-metric-group-title">{group.title}</div>
                    <div className="business-dashboard-metric-grid">
                        {group.items.map((item) => {
                            const muted = isNil(item.value) || item.value === "--";
                            return (
                                <div className="business-dashboard-metric-cell" key={item.label}>
                                    <div className="business-dashboard-metric-label">
                                        <span title={item.label}>{item.label}</span>
                                        {item.tag ? (
                                            <Tag color={item.tagColor || "gold"} style={{ marginInlineEnd: 0 }}>
                                                {item.tag}
                                            </Tag>
                                        ) : null}
                                    </div>
                                    <div className={`business-dashboard-metric-value${muted ? " is-muted" : ""}`}>{muted ? "--" : item.value}</div>
                                    {item.sub ? <div className="business-dashboard-metric-sub">{item.sub}</div> : null}
                                </div>
                            );
                        })}
                    </div>
                </div>
            ))}
        </div>
    );
}

// 航线集中度面板：横向 BH 排名条形 + 两个标量集中度口径（最大航线占比 / 等效航线数）。
// 不画单条/累计占比——前者只是 BH 量重排、后者航线一多就糊且维护成本高；
// 「是否过度依赖少数航线」用最大单航线占比与 1÷HHI 两个标量回答（规划方案 5.23）。
// 最大航线占比软阈值：>40% 标「过度依赖」、>25% 标「偏集中」（货运/包机体量小、集中本属常态，仅提示不计红黄绿）。
// 入参为「整圈航线」（销售航线主数据，如 CGO-HSA-BUD-CGO）的全机队聚合，BH 口径取整圈总 BH。
function RouteConcentrationPanel({ loops, periodMeta }) {
    const model = useMemo(() => {
        const total = sumBy(loops, "total_bh");
        const sorted = (loops || [])
            .map((row) => ({ label: row.route_code || "--", bh: Number(row.total_bh) || 0 }))
            .filter((row) => row.bh > 0)
            .sort((a, b) => b.bh - a.bh);
        if (!total || !sorted.length) {
            return { rows: [], maxShare: null, equivRoutes: null, routeCount: 0 };
        }
        const withShare = sorted.map((row) => ({ ...row, share: row.bh / total }));
        const hhi = withShare.reduce((sum, row) => sum + row.share * row.share, 0);
        return {
            rows: withShare.slice(0, CONCENTRATION_TOP_ROUTES),
            maxShare: withShare[0].share,
            equivRoutes: hhi > 0 ? 1 / hhi : null,
            routeCount: sorted.length,
        };
    }, [loops]);

    const maxShareTag = isNil(model.maxShare) ? {} : model.maxShare > 0.4 ? { tag: "过度依赖", tagColor: "red" } : model.maxShare > 0.25 ? { tag: "偏集中", tagColor: "gold" } : {};

    const indexGroups = [
        {
            title: "集中度口径",
            items: [
                { label: "在飞整圈数", value: model.routeCount ? `${formatNumber(model.routeCount)} 条` : "--" },
                { label: "最大整圈占比", value: isNil(model.maxShare) ? "--" : formatPercent(model.maxShare), ...maxShareTag },
                { label: "等效航线数", value: isNil(model.equivRoutes) ? "--" : `${formatNumber(model.equivRoutes, 1)} 条`, sub: "1÷HHI · 越小越集中" },
            ],
        },
    ];

    const chartHeight = Math.min(Math.max(model.rows.length * 30 + 32, 200), 520);
    const extraText = model.routeCount > model.rows.length ? `Top ${model.rows.length} · 共 ${formatNumber(model.routeCount)} 条` : "识别航线依赖风险";

    return (
        <PanelCard title="航线集中度（整圈 · 按实际 BH 排名）" extra={<Space size={6}><Tag color="blue">{periodMeta?.label}口径</Tag><Text type="secondary">{extraText}</Text></Space>}>
            {model.rows.length ? (
                <>
                    <MetricSlotGrid groups={indexGroups} />
                    <EChart data={model.rows} optionFactory={routeConcentrationOption} height={chartHeight} />
                </>
            ) : (
                <DashboardEmpty description="当前筛选条件下暂无整圈航线 BH 数据（仅统计可匹配销售航线的航段）" />
            )}
        </PanelCard>
    );
}

const COMPOSITION_COLORS = ["#2b6cff", "#15a35a", "#f5a623", "#7b61ff", "#00b8d9", "#e2483d", "#ff7a45", "#8c9bb5"];

// 单条 100% 分段占比条 + 图例，把分类构成压缩到一行，信息密度高于多条形图。
function CompositionBar({ title, data, unit = "架", placeholder }) {
    const total = (data || []).reduce((sum, item) => sum + (Number(item.count) || 0), 0);
    return (
        <div className="business-dashboard-compose-row">
            <div className="business-dashboard-compose-head">
                <span className="business-dashboard-compose-title">{title}</span>
                {placeholder ? <Tag color="gold">待接入</Tag> : <span className="business-dashboard-compose-total">{formatNumber(total)} {unit}</span>}
            </div>
            {placeholder || !total ? (
                <div className="business-dashboard-compose-empty">{placeholder || "暂无数据"}</div>
            ) : (
                <>
                    <div className="business-dashboard-compose-track">
                        {data.map((item, idx) => (
                            <span
                                key={item.label}
                                className="business-dashboard-compose-seg"
                                style={{ width: `${item.ratio * 100}%`, background: COMPOSITION_COLORS[idx % COMPOSITION_COLORS.length] }}
                                title={`${item.label} · ${formatNumber(item.count)} ${unit} · ${formatPercent(item.ratio)}`}
                            />
                        ))}
                    </div>
                    <div className="business-dashboard-compose-legend">
                        {data.map((item, idx) => (
                            <span className="business-dashboard-compose-legend-item" key={item.label}>
                                <i style={{ background: COMPOSITION_COLORS[idx % COMPOSITION_COLORS.length] }} />
                                <span className="business-dashboard-compose-legend-label" title={item.label}>{item.label}</span>
                                <em>{formatNumber(item.count)} · {formatPercent(item.ratio)}</em>
                            </span>
                        ))}
                    </div>
                </>
            )}
        </div>
    );
}

// 单机卡状态的中文标签与语义色。取值来自 current-status 接口：
// 运行态（DELAYED/IN_FLIGHT/IN_PROGRESS/PLANNED 及航班状态）优先，否则回退飞机主数据状态。
// 主数据状态标签与 /master-data/aircraft 页面对齐：ACTIVE=启用、其余视作停用语义
//（MAINTENANCE/RESERVED 为模型历史遗留值，如实展示）。未知值原文展示、默认色兜底。
const AIRCRAFT_STATUS_META = {
    IN_FLIGHT: { label: "在飞", color: "blue" },
    DEPARTED: { label: "在飞", color: "blue" },
    DELAYED: { label: "延误", color: "red" },
    IN_PROGRESS: { label: "执行中", color: "blue" },
    SCHEDULED: { label: "已排班", color: "cyan" },
    PLANNED: { label: "待执行", color: "cyan" },
    ACTIVE: { label: "启用", color: "green" },
    MAINTENANCE: { label: "维修", color: "gold" },
    RESERVED: { label: "预留", color: "purple" },
    INACTIVE: { label: "停用", color: "default" },
};

const aircraftStatusMeta = (status) => {
    if (!status) return { label: "--", color: "default" };
    return AIRCRAFT_STATUS_META[String(status).toUpperCase()] || { label: status, color: "default" };
};

// 单机在没有不可用窗口时，接口仍会给出 100%。这是兜底数，不是机队可用率。
// 机队可用率不把这些飞机算进去。这里也不拿这个 100% 给卡片涂红黄绿，避免整面假绿。
const isFallbackAvailability = (avail) => Boolean(avail && !isNil(avail.availability_rate) && !Number(avail.window_count));

// 「运营中」判定：主数据状态 ACTIVE，或当前有在飞/待飞航段（有活动就算在营，
// 避免主数据没维护把正在飞的飞机误置灰）。非运营机整卡置灰、排序沉底。
const isAircraftOperating = (item) => {
    if (item.current_leg || item.next_leg) return true;
    return String(item.master_status || "ACTIVE").toUpperCase() === "ACTIVE";
};

const aircraftCardTone = (avail) => {
    if (!avail || isNil(avail.availability_rate) || isFallbackAvailability(avail)) return "info";
    return rateTone(avail.availability_rate, THRESHOLDS.availability);
};

const AC_TONE_SEVERITY = { red: 0, amber: 1 };

function AircraftTab({ data, loading, error, onRetry, periodMeta }) {
    const prefix = periodMeta.prefix;
    const currentItems = data?.currentStatus?.items || [];
    const bhItems = data?.bhByAircraft?.items || [];
    const availability = data?.availability?.items || [];
    const operatorGroups = data?.operatorGroups?.items || [];
    const availabilityByReg = useMemo(() => new Map(availability.map((row) => [row.aircraft_reg, row])), [availability]);
    const bhByReg = useMemo(() => new Map(bhItems.map((row) => [row.aircraft_reg, row])), [bhItems]);

    // 单机卡排序：运营中在前、非运营（停用/预留/无航段的维修机）沉底；
    // 同组内红 → 黄 → 绿/无数据，再按 AOG 影响时长降序，最后按注册号，
    // 让需要关注的飞机始终排在首屏（后端默认按注册号字母序返回）。
    const sortedCurrentItems = useMemo(() => {
        const severity = (item) => AC_TONE_SEVERITY[aircraftCardTone(availabilityByReg.get(item.aircraft_reg))] ?? 2;
        const aogHours = (item) => Number(availabilityByReg.get(item.aircraft_reg)?.aog_impact_hours) || 0;
        return [...currentItems].sort((a, b) => {
            const operatingDiff = Number(isAircraftOperating(b)) - Number(isAircraftOperating(a));
            if (operatingDiff) return operatingDiff;
            const sevDiff = severity(a) - severity(b);
            if (sevDiff) return sevDiff;
            const aogDiff = aogHours(b) - aogHours(a);
            if (aogDiff) return aogDiff;
            return String(a.aircraft_reg || "").localeCompare(String(b.aircraft_reg || ""));
        });
    }, [currentItems, availabilityByReg]);
    const operatingCurrentItems = useMemo(
        () => sortedCurrentItems.filter(isAircraftOperating),
        [sortedCurrentItems],
    );

    const typeDistribution = useMemo(() => buildDistribution(currentItems, (item) => item.aircraft_type), [currentItems]);
    const statusDistribution = useMemo(
        () => buildDistribution(currentItems, (item) => {
            const status = item.status || item.master_status;
            return status ? aircraftStatusMeta(status).label : null;
        }),
        [currentItems],
    );
    const operatorDistribution = useMemo(() => {
        const total = operatorGroups.reduce((sum, group) => sum + (Number(group.aircraft_count) || 0), 0);
        return operatorGroups
            .map((group) => {
                const count = Number(group.aircraft_count) || 0;
                return {
                    label: group.operator_name || group.operator_code || "未分配",
                    count,
                    ratio: total ? count / total : 0,
                };
            })
            .sort((a, b) => b.count - a.count);
    }, [operatorGroups]);

    // 机队总览：飞机资产层按文档以单机为对象，合计/平均会掩盖个体问题（5.8/5.16 均要求按单机判断）。
    // 故顶部只放"规模 + 异常计数"，把问题机点名，平均/完成率交由管理层总览(4.1)展示。
    const fleetKpiGroups = useMemo(() => {
        const totalActual = sumBy(bhItems, "actual_bh");
        const aogAffected = (availability || []).filter((row) => Number(row.aog_impact_hours) > 0).length;
        const totalAogHours = sumBy(availability, "aog_impact_hours");
        const recordedAvailability = (availability || []).filter((row) => Number(row.window_count) > 0);
        const availabilityUnrecorded = (availability || []).length > 0 && recordedAvailability.length === 0;
        const lowAvail = recordedAvailability.filter((row) => !isNil(row.availability_rate) && Number(row.availability_rate) < 0.9).length;
        return [
            {
                title: `机队总览（${periodMeta.label}口径）`,
                items: [
                    { label: "在管机队", value: currentItems.length ? `${formatNumber(currentItems.length)} 架` : "--" },
                    { label: "AOG 影响架数", value: availability.length ? `${formatNumber(aogAffected)} 架` : "--", sub: availability.length && totalAogHours ? `影响 ${formatHours(totalAogHours)}` : undefined, ...(availability.length && !totalAogHours ? { tag: "待接入", tagColor: "gold" } : {}) },
                    {
                        label: "可用率<90% 架数",
                        value: availabilityUnrecorded ? "--" : availability.length ? `${formatNumber(lowAvail)} 架` : "--",
                        tag: availabilityUnrecorded ? "未记录" : lowAvail ? "需关注" : undefined,
                        tagColor: availabilityUnrecorded ? "default" : "red",
                        sub: availabilityUnrecorded ? "本窗口没有不可用记录" : undefined,
                    },
                    { label: "机队总实际 BH", value: bhItems.length ? formatHours(totalActual) : "--" },
                ],
            },
        ];
    }, [bhItems, availability, currentItems, periodMeta]);

    // 缺数据源、只能占位的规划指标，统一收到底部一块，避免淹没真实数据。
    const fleetWaitingGroups = useMemo(() => {
        const waiting = { tag: "待接入", value: "--" };
        return [
            {
                title: "运力口径",
                items: [
                    { label: "调机 BH 占比", ...waiting },
                    { label: "BH 多口径（商业/调机/锁定/可售/未售/承诺/托管）", ...waiting },
                    { label: "单机可售航班量", ...waiting, sub: "缺可销售运力/不可销售占用模型" },
                    { label: "预计航班量·高概率预测部分", ...waiting, sub: "预计现仅含已确认订单" },
                ],
            },
            {
                title: "AOG 明细",
                items: [
                    { label: "AOG 次数", ...waiting },
                    { label: "技术 AOG 时长", ...waiting },
                    { label: "AOG 原因分类", ...waiting },
                    { label: "AOG 影响航班数 / 取消数", ...waiting },
                ],
            },
            {
                title: "维修",
                items: [
                    { label: "MTTR 平均故障恢复时间", ...waiting },
                    { label: "重复故障率", ...waiting },
                    { label: "下次维修检型（A/C/D 检）", value: "--", tag: "仅日期", tagColor: "blue", sub: "现仅 next_inspection_date，无检型" },
                ],
            },
        ];
    }, []);

    const columns = [
        { title: "飞机", dataIndex: "aircraft_reg", key: "aircraft_reg", fixed: "left" },
        { title: "BH 日均", dataIndex: "avg_daily_utilization", key: "avg_daily_utilization", render: formatHours },
        { title: `${prefix}计划 BH`, dataIndex: "planned_bh", key: "planned_bh", render: formatHours },
        { title: `${prefix}实际 BH`, dataIndex: "actual_bh", key: "actual_bh", render: formatHours },
        { title: "BH 完成率", dataIndex: "completion_rate", key: "completion_rate", render: formatPercent },
        { title: "计划航段", dataIndex: "planned_leg_count", key: "planned_leg_count", render: formatNumber },
        { title: "实际航段", dataIndex: "actual_leg_count", key: "actual_leg_count", render: formatNumber },
        { title: `${prefix}小时循环比`, dataIndex: "fh_per_cycle", key: "fh_per_cycle", render: (value) => (isNil(value) ? "--" : formatNumber(value, 2)) },
    ];

    return (
        <div>
            <BlockError error={error} onRetry={onRetry} />
            <Spin spinning={loading}>
                <PanelCard title="机队总览" extra={<Space size={6}><Tag color="blue">{periodMeta.label}口径</Tag><Text type="secondary">{periodMeta.utilizationNote}</Text></Space>}>
                    <MetricSlotGrid groups={fleetKpiGroups} />
                </PanelCard>
                <PanelCard title="单机资产卡" extra={<Text type="secondary">仅展示运营中飞机 · 异常机优先</Text>}>
                    <div className="business-dashboard-aircraft-grid">
                        {operatingCurrentItems.length ? (
                            operatingCurrentItems.map((item) => <AircraftStatusCard key={item.aircraft_reg} item={item} avail={availabilityByReg.get(item.aircraft_reg)} bh={bhByReg.get(item.aircraft_reg)} periodMeta={periodMeta} />)
                        ) : (
                            <Card>
                                <DashboardEmpty description="当前筛选条件下暂无飞机状态" />
                            </Card>
                        )}
                    </div>
                </PanelCard>
                <PanelCard title="机队构成" extra={<Text type="secondary">基于当前在管机队 · 单机 BH 见下方明细</Text>}>
                    <div className="business-dashboard-compose-list">
                        <CompositionBar title="按机型" data={typeDistribution} />
                        <CompositionBar title="按当前状态" data={statusDistribution} />
                        <CompositionBar title="按运营航司" data={operatorDistribution} />
                        <CompositionBar title="按所有权" placeholder="所有权数据源待接入" />
                    </div>
                </PanelCard>
                <PanelCard title="单机 BH 明细" extra={<Text type="secondary">航段完成率 = 实际航段 ÷ 计划航段</Text>}>
                    <Table rowKey="aircraft_reg" size="small" columns={columns} dataSource={bhItems} pagination={false} scroll={{ x: 860 }} locale={{ emptyText: tableEmptyText("当前筛选条件下暂无单机 BH 数据") }} />
                </PanelCard>
                <PanelCard title="规划待接入指标" extra={<Tag color="gold">缺数据源</Tag>}>
                    <MetricSlotGrid groups={fleetWaitingGroups} />
                </PanelCard>
            </Spin>
        </div>
    );
}

const AIRCRAFT_QUALITY_META = {
    COMPLETE: { label: "数据完整", className: "is-complete" },
    PARTIAL: { label: "数据部分缺失", className: "is-partial" },
    MISSING: { label: "数据缺失", className: "is-missing" },
};

function AircraftStatusCard({ item, avail, bh, periodMeta }) {
    const leg = item.current_leg || item.next_leg || item.latest_completed_leg;
    const legLabel = item.current_leg ? "当前" : item.next_leg ? "下一" : "最近";
    const statusMeta = aircraftStatusMeta(item.status || item.master_status);
    const operator = item.operator_name || item.operator_code;
    const availabilityRate = avail?.availability_rate;
    // 可用率不再占卡面空间：实测值收进角标圆点的 tooltip，兜底值（无停场记录恒 100%）不展示。
    const hasMeasuredAvailability = !isNil(availabilityRate) && !isFallbackAvailability(avail);
    const tone = aircraftCardTone(avail);
    const aogHours = avail?.aog_impact_hours;
    const qualityMeta = AIRCRAFT_QUALITY_META[item.data_quality_level];
    const operating = isAircraftOperating(item);
    // 周期内没飞的飞机在 bh/by-aircraft 里没有条目，事实是 0 段 / 0h 而非数据缺失。
    const plannedLegs = bh?.planned_commercial_flights ?? 0;
    const actualLegs = bh?.actual_commercial_flights ?? 0;

    return (
        <div className={`business-dashboard-ac-card business-dashboard-ac-card--${tone}${operating ? "" : " business-dashboard-ac-card--offline"}`}>
            <div className="business-dashboard-ac-top">
                <span className="business-dashboard-ac-reg-wrap">
                    <span className="business-dashboard-ac-reg">{item.aircraft_reg}</span>
                    {hasMeasuredAvailability ? (
                        <Tooltip title={`可用率 ${formatPercent(availabilityRate)}（红黄绿依据）`}>
                            <span className="business-dashboard-ac-dot" />
                        </Tooltip>
                    ) : (
                        <span className="business-dashboard-ac-dot" aria-hidden="true" />
                    )}
                </span>
                <Tag color={statusMeta.color} style={{ marginInlineEnd: 0 }}>{statusMeta.label}</Tag>
            </div>
            <div className="business-dashboard-ac-sub">{[item.aircraft_type, operator].filter(Boolean).join(" · ") || "--"}</div>
            <div className="business-dashboard-ac-leg-line">
                <span className="business-dashboard-ac-leg-label">{legLabel}</span>
                {leg ? (
                    <span className="business-dashboard-ac-route">
                        <span>{leg.origin || "--"}</span>
                        <i>→</i>
                        <span>{leg.destination || "--"}</span>
                        {leg.flight_number ? <em>{leg.flight_number}</em> : null}
                    </span>
                ) : (
                    <span className="business-dashboard-ac-route business-dashboard-ac-route--empty">暂无航段</span>
                )}
                {leg?.business_date ? <span className="business-dashboard-ac-leg-date">{dayjs(leg.business_date).format("MM-DD")}</span> : null}
            </div>
            <div className="business-dashboard-ac-stats">
                <div className="business-dashboard-ac-stat">
                    <span className="business-dashboard-ac-stat-label" title={`${periodMeta.prefix}实际 BH`}>实际 BH</span>
                    <span className="business-dashboard-ac-stat-val">{formatHours(bh?.actual_bh ?? 0)}</span>
                </div>
                <div className="business-dashboard-ac-stat">
                    <span className="business-dashboard-ac-stat-label" title="实际 BH ÷ 周期天数">BH 日均</span>
                    <span className="business-dashboard-ac-stat-val">{formatHours(bh?.avg_daily_utilization ?? 0)}</span>
                </div>
                <div className="business-dashboard-ac-stat">
                    <span className="business-dashboard-ac-stat-label" title={`${periodMeta.prefix}实际航段 ÷ 预计航段（含调机）`}>航段 实/计</span>
                    <span className="business-dashboard-ac-stat-val">{formatNumber(actualLegs)}/{formatNumber(plannedLegs)}</span>
                </div>
                <div className="business-dashboard-ac-stat">
                    <span className="business-dashboard-ac-stat-label" title="实际 BH ÷ 计划 BH">完成率</span>
                    <span className="business-dashboard-ac-stat-val">{isNil(bh?.completion_rate) ? "--" : formatPercent(bh.completion_rate)}</span>
                </div>
            </div>
            <div className="business-dashboard-ac-foot">
                <span>下次检查 {formatDate(item.next_inspection_date)}</span>
                <span className="business-dashboard-ac-foot-right">
                    {!isNil(aogHours) && Number(aogHours) > 0 ? <span className="business-dashboard-ac-foot-aog">AOG {formatNumber(aogHours, 1)}h</span> : null}
                    {qualityMeta ? (
                        <Tooltip title="航段与配置数据的完整度（来自当前状态接口）">
                            <span className={`business-dashboard-ac-quality ${qualityMeta.className}`}>
                                <i aria-hidden="true" />
                                {qualityMeta.label}
                            </span>
                        </Tooltip>
                    ) : null}
                </span>
            </div>
        </div>
    );
}

function RoutesTab({ data, loading, error, onRetry, periodMeta }) {
    const routes = data?.routesPerformance?.items || [];
    const otp = data?.routesOtp?.items || [];
    // 主表改为「整圈航线」维度（对齐结算「按航线」口径，仅统计能匹配销售航线的航段），
    // 与上方 OD 维度的网络图 / OD 矩阵 / 准点散点解耦：后者看机场对，前者看整圈执行量与集中度。
    const routesLoop = data?.routesLoop?.items || [];
    const cargoRoutes = data?.cargoByRoute?.items || [];
    const ratedCargoRoutes = cargoRoutes.filter((row) => Number(row.capacity_kg) > 0 && !isNil(row.weight_load_factor));
    const ratedCapacity = sumBy(ratedCargoRoutes, "capacity_kg");
    const ratedWeight = ratedCargoRoutes.reduce((sum, row) => sum + Number(row.weight_load_factor) * Number(row.capacity_kg), 0);
    const routeWeightFactor = ratedCapacity > 0 ? ratedWeight / ratedCapacity : null;
    const loadedPositions = sumBy(cargoRoutes, "loaded_position_count");
    const emptyPositions = sumBy(cargoRoutes, "empty_position_count");
    const routeUldFactor = loadedPositions + emptyPositions > 0 ? loadedPositions / (loadedPositions + emptyPositions) : null;

    // 概览 KPI（全部实测可算）。机队级完成率用加权口径（Σ实际 ÷ Σ计划），比对各航线简单平均更能反映整体真实完成度。
    // BH 完成率已在「管理层总览」顶部 KPI 展示，此处不再重复，仅保留航线特有的航段完成率。
    const totalActualBh = sumBy(routes, "actual_bh");
    const totalPlannedLegs = sumBy(routes, "planned_leg_count");
    const totalActualLegs = sumBy(routes, "actual_leg_count");
    const totalCompleted = sumBy(otp, "completed_flight_count");
    const legCompletion = totalPlannedLegs ? totalActualLegs / totalPlannedLegs : null;
    const weightedDepDelay = totalCompleted
        ? otp.reduce((sum, row) => sum + (Number(row.avg_departure_delay_minutes) || 0) * (Number(row.completed_flight_count) || 0), 0) / totalCompleted
        : null;

    const overviewMetricGroups = [
        {
            title: "运量与执行",
            items: [
                { label: `${periodMeta.prefix}在飞机场对`, value: routes.length ? `${formatNumber(routes.length)} 对` : "--", sub: "OD 机场对口径" },
                { label: `${periodMeta.prefix}总实际 BH`, value: routes.length ? formatHours(totalActualBh) : "--" },
                { label: `${periodMeta.prefix}完成航班数`, value: otp.length ? `${formatNumber(totalCompleted)} 班` : "--" },
                { label: `${periodMeta.prefix}平均离港延误`, value: isNil(weightedDepDelay) ? "--" : `${formatNumber(weightedDepDelay, 0)} min`, ...(!isNil(weightedDepDelay) && Number(weightedDepDelay) > 60 ? { tag: "偏高", tagColor: "red" } : {}) },
                { label: `${periodMeta.prefix}调机 BH 占比`, tag: "待接入", value: "--", sub: "需后端按 stc_type 拆 FERRY" },
            ],
        },
    ];

    // 规划占位区：缺数据源的航线指标统一收口在底部，明确标注待接入原因，避免空表占据主版面。
    const routeWaitingGroups = [
        {
            title: "装载利用（有报文的航段）",
            items: [
                {
                    label: "业载利用率（重量）",
                    value: isNil(routeWeightFactor) ? "--" : formatPercent(routeWeightFactor),
                    ...(cargoRoutes.length === 0
                        ? { tag: "待接入", sub: "本窗口没有货载报文" }
                        : isNil(routeWeightFactor)
                          ? { tag: "缺货运能力", sub: "只对填了货运能力的飞机计算" }
                          : { sub: "不含没有货运能力的航段" }),
                },
                { label: "体积利用率", tag: "待接入", value: "--", sub: "缺有效可用装载体积" },
                {
                    label: "板位利用率",
                    value: isNil(routeUldFactor) ? "--" : formatPercent(routeUldFactor),
                    ...(isNil(routeUldFactor) ? { tag: "待接入", sub: cargoRoutes.length ? "缺报文可用板位" : "本窗口没有货载报文" } : { sub: "有舱位报文的航段" }),
                },
            ],
        },
        {
            title: "周转与网络效率",
            items: [
                { label: "AFTK 可用吨公里", tag: "待接入", value: "--" },
                { label: "FTK 实际周转量", tag: "待接入", value: "--" },
                { label: "调机 BH 占比", tag: "待接入", value: "--", sub: "需后端按 stc_type 拆 FERRY" },
                { label: "回程利用率", tag: "待接入", value: "--" },
            ],
        },
        {
            title: "运行归因与收入",
            items: [
                { label: "延误原因 Top5（帕累托）", tag: "待接入", value: "--", sub: "delay_reason 实测为空" },
                { label: "延误双口径（班数/分钟）", tag: "待接入", value: "--" },
                { label: "航线收入 / 毛利", tag: "待接入", value: "--", sub: "需销售合同打通 + 金额权限" },
            ],
        },
    ];

    // 整圈航线主表列：航线即完整航圈（机场序列），右侧为执行量与 BH。货载 / 完成率是「机场对（OD）」
    // 口径，在整圈维度无法直接合并，故不在本表展示（相关项见下方占位区）。
    const loopTotalBh = sumBy(routesLoop, "total_bh");
    const loopColumns = [
        {
            title: "航线（整圈）",
            dataIndex: "route_code",
            key: "route_code",
            fixed: "left",
            width: 220,
            render: (value) => <Text strong>{value || "--"}</Text>,
        },
        { title: "航段数", dataIndex: "route_legs", key: "route_legs", align: "right", width: 84, render: formatNumber },
        { title: "执行次数", dataIndex: "route_instance_count", key: "route_instance_count", align: "right", width: 96, render: (value) => `${formatNumber(value)} 次` },
        { title: "涉及飞机", dataIndex: "aircraft_count", key: "aircraft_count", align: "right", width: 92, render: (value) => `${formatNumber(value)} 架` },
        {
            title: "总实际 BH",
            dataIndex: "total_bh",
            key: "total_bh",
            align: "right",
            width: 132,
            sorter: (a, b) => (Number(a.total_bh) || 0) - (Number(b.total_bh) || 0),
            render: (value) => (
                <span>
                    {formatHours(value)}
                    {loopTotalBh ? <Text type="secondary" style={{ marginLeft: 6 }}>{formatPercent((Number(value) || 0) / loopTotalBh)}</Text> : null}
                </span>
            ),
        },
        { title: "平均每趟 BH", dataIndex: "avg_bh", key: "avg_bh", align: "right", width: 116, render: formatHours },
    ];

    return (
        <div>
            <BlockError error={error} onRetry={onRetry} />
            <Spin spinning={loading}>
                <PanelCard title="航线经营概览" extra={<Space size={6}><Tag color="blue">{periodMeta.label}口径</Tag><Text type="secondary">机队级加权口径</Text></Space>}>
                    <div className="business-dashboard-threshold-list">
                        <ThresholdBar label={`${periodMeta.prefix}航段完成率`} value={legCompletion} thresholds={THRESHOLDS.flightCompletion} target={THRESHOLDS.flightCompletion.green} subText={`实际 ${formatNumber(totalActualLegs)} ÷ 计划 ${formatNumber(totalPlannedLegs)} 段`} />
                    </div>
                    <MetricSlotGrid groups={overviewMetricGroups} />
                </PanelCard>

                <Row gutter={[16, 16]}>
                    <Col xs={24} xl={14}>
                        <PanelCard title="航线网络态势" extra={<Tag color="cyan">Network</Tag>} className="business-dashboard-map-card">
                            <RouteNetworkMap routes={routes} />
                        </PanelCard>
                    </Col>
                    <Col xs={24} xl={10}>
                        <RouteConcentrationPanel loops={routesLoop} periodMeta={periodMeta} />
                    </Col>
                </Row>

                <PanelCard title="航线经营主表（整圈航线）" extra={<Text type="secondary">整圈口径 · 展开看航段构成与按机明细</Text>}>
                    <Table
                        rowKey="route_code"
                        size="small"
                        columns={loopColumns}
                        dataSource={routesLoop}
                        pagination={{ pageSize: 12 }}
                        scroll={{ x: 760 }}
                        expandable={{
                            expandedRowRender: (record) => <RouteLoopExpand record={record} />,
                            rowExpandable: (record) => (record.aircraft_breakdown?.length || 0) > 0,
                        }}
                        locale={{ emptyText: tableEmptyText("当前筛选条件下暂无整圈航线数据（仅统计可匹配销售航线的航段）") }}
                    />
                </PanelCard>

                <PanelCard title="规划待接入指标" extra={<Tag color="gold">缺数据源</Tag>}>
                    <MetricSlotGrid groups={routeWaitingGroups} />
                </PanelCard>
            </Spin>
        </div>
    );
}

// 整圈航线展开行：航段构成（机场序列）+ 哪些飞机飞了它、各飞了几次 / 多少 BH。
// 替代旧的「按 OD route_code 调下钻接口」抽屉——整圈 route_code（CGO-HSA-BUD-CGO）不是机场对，
// 套不进按机场对过滤的 BH 趋势 / 航段 / 事件接口；按机明细已随整圈聚合返回，无需再发请求。
function RouteLoopExpand({ record }) {
    const airports = (record?.route_code || "").split("-").filter(Boolean);
    const breakdown = record?.aircraft_breakdown || [];
    return (
        <div className="business-dashboard-stack-panel">
            <Space size={4} wrap align="center">
                <Text type="secondary" style={{ marginRight: 4 }}>航段构成</Text>
                {airports.length
                    ? airports.map((code, idx) => (
                          <span key={`${code}-${idx}`}>
                              <Tag color="blue" style={{ marginRight: 4 }}>{code}</Tag>
                              {idx < airports.length - 1 ? <Text type="secondary" style={{ marginRight: 4 }}>→</Text> : null}
                          </span>
                      ))
                    : <Text type="secondary">--</Text>}
            </Space>
            <Table
                rowKey="aircraft_reg"
                size="small"
                pagination={false}
                dataSource={breakdown}
                locale={{ emptyText: tableEmptyText("暂无按机明细") }}
                columns={[
                    { title: "飞机", dataIndex: "aircraft_reg", key: "aircraft_reg" },
                    { title: "执行次数", dataIndex: "route_instance_count", key: "route_instance_count", align: "right", render: (value) => `${formatNumber(value)} 次` },
                    { title: "总实际 BH", dataIndex: "total_bh", key: "total_bh", align: "right", render: formatHours },
                    { title: "平均每趟 BH", dataIndex: "avg_bh", key: "avg_bh", align: "right", render: formatHours },
                ]}
            />
        </div>
    );
}

function CustomersTab({ data, loading, error, onRetry, requestParams, periodMeta, salesSummary, hasAmountPermission }) {
    const { selected: selectedCustomer, open: openCustomer, close: closeCustomer } = useDrillDown();
    const customers = data?.customers?.items || [];
    const commercialFlights = data?.commercialFlights?.items || [];

    const customerMetricGroups = useMemo(() => {
        const waiting = { tag: "待接入", value: "--" };
        const avgFulfillment = avgBy(customers, "fulfillment_rate");
        const avgCancel = avgBy(customers, "customer_cancel_rate");
        return [
            {
                title: "履约",
                items: [
                    { label: "客户数", value: customers.length ? `${formatNumber(customers.length)} 个` : "--" },
                    { label: "平均履约率", value: isNil(avgFulfillment) ? "--" : formatPercent(avgFulfillment) },
                    { label: "平均取消率", value: isNil(avgCancel) ? "--" : formatPercent(avgCancel) },
                    { label: "客户总实际 BH", value: customers.length ? formatHours(sumBy(customers, "actual_bh")) : "--" },
                ],
            },
            {
                title: "异常与风险",
                items: [
                    { label: "客户原因异常率", ...waiting },
                    { label: "公司原因异常率", ...waiting },
                    { label: "客户风险等级（红黄绿规则）", ...waiting },
                ],
            },
            {
                title: "拉货",
                items: [
                    { label: "拉货航班数", ...waiting },
                    { label: "拉货重量", ...waiting },
                    { label: "拉货率", ...waiting },
                    { label: "拉货原因分类", ...waiting },
                ],
            },
            {
                title: "未来",
                items: [{ label: "客户未来锁定 BH", ...waiting }],
            },
        ];
    }, [customers]);

    return (
        <div>
            <BlockError error={error} onRetry={onRetry} />
            <Spin spinning={loading}>
                <Row gutter={[16, 16]}>
                    <Col span={24}>
                        <PanelCard title="客户经营汇总" extra={<Space size={6}><Tag color="blue">{periodMeta.label}口径</Tag><Text type="secondary">规划指标位 · 缺数据源处占位 --</Text></Space>}>
                            <MetricSlotGrid groups={customerMetricGroups} />
                        </PanelCard>
                    </Col>
                    {hasAmountPermission && (
                        <Col span={24}>
                            <PanelCard title="销售与合同明细" extra={<Tag color="blue">金额权限</Tag>}>
                                <SalesDetailPanel salesSummary={salesSummary} />
                            </PanelCard>
                        </Col>
                    )}
                    <Col span={24}>
                        <PanelCard title="客户履约矩阵" extra={<Text type="secondary">点击客户卡查看履约详情</Text>}>
                            {customers.length ? (
                                <CustomerMatrix customers={customers} onSelect={openCustomer} />
                            ) : (
                                <DashboardEmpty description="当前筛选条件下暂无客户履约数据" />
                            )}
                        </PanelCard>
                    </Col>
                    <Col span={24}>
                        <PanelCard title="商业 Flight 样本">
                            <Table
                                rowKey="commercial_flight_key"
                                size="small"
                                dataSource={commercialFlights}
                                pagination={{ pageSize: 8 }}
                                scroll={{ x: 980 }}
                                locale={{ emptyText: tableEmptyText("当前筛选条件下暂无商业 Flight 样本") }}
                                columns={[
                                    { title: "商业 Flight", dataIndex: "commercial_flight_key", key: "commercial_flight_key" },
                                    { title: "客户", dataIndex: "customer_name", key: "customer_name" },
                                    { title: "起点", dataIndex: "commercial_origin", key: "commercial_origin" },
                                    { title: "终点", dataIndex: "commercial_destination", key: "commercial_destination" },
                                    { title: "状态", dataIndex: "status", key: "status", render: (value) => <Tag>{value}</Tag> },
                                    { title: "实际 BH", dataIndex: "actual_bh", key: "actual_bh", render: formatHours },
                                    { title: "收入上下文", dataIndex: "revenue_context_status", key: "revenue_context_status", render: (value) => <Tag color={value === "AVAILABLE" ? "green" : "gold"}>{value || "MISSING"}</Tag> },
                                ]}
                            />
                        </PanelCard>
                    </Col>
                </Row>
            </Spin>
            <CustomerFulfillmentDrawer customer={selectedCustomer} requestParams={requestParams} onClose={closeCustomer} />
        </div>
    );
}

function CustomerFulfillmentDrawer({ customer, requestParams, onClose }) {
    const [detail, setDetail] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        let alive = true;
        const load = async () => {
            if (!customer?.customer_id) {
                setDetail(null);
                return;
            }
            setLoading(true);
            try {
                const result = await businessDashboardApi.getCustomerFulfillment(customer.customer_id, requestParams);
                if (alive) setDetail(result);
            } catch (error) {
                message.error("客户履约详情加载失败");
                if (alive) setDetail(null);
            } finally {
                if (alive) setLoading(false);
            }
        };
        load();
        return () => {
            alive = false;
        };
    }, [customer, requestParams]);

    return (
        <DrillDownDrawer title={customer?.customer_name || customer?.customer_id || "客户履约详情"} open={Boolean(customer)} onClose={onClose} loading={loading}>
                {detail ? (
                    <>
                        <Descriptions column={2} bordered size="small">
                            <Descriptions.Item label="计划 Flight">{formatNumber(detail.performance?.planned_commercial_flights)}</Descriptions.Item>
                            <Descriptions.Item label="实际 Flight">{formatNumber(detail.performance?.actual_commercial_flights)}</Descriptions.Item>
                            <Descriptions.Item label="履约率">{formatPercent(detail.performance?.fulfillment_rate)}</Descriptions.Item>
                            <Descriptions.Item label="实际 BH">{formatHours(detail.performance?.actual_bh)}</Descriptions.Item>
                        </Descriptions>
                        <Table
                            className="business-dashboard-drawer-table"
                            rowKey="commercial_flight_key"
                            size="small"
                            dataSource={detail.commercial_flights || []}
                            pagination={false}
                            columns={[
                                { title: "商业 Flight", dataIndex: "commercial_flight_key", key: "commercial_flight_key" },
                                { title: "航线", key: "route", render: (_, record) => `${record.commercial_origin || "--"} → ${record.commercial_destination || "--"}` },
                                { title: "状态", dataIndex: "status", key: "status" },
                                { title: "实际 BH", dataIndex: "actual_bh", key: "actual_bh", render: formatHours },
                            ]}
                        />
                    </>
                ) : (
                    <DashboardEmpty description="暂无客户履约详情" hint="请选择一行客户履约数据查看详情。" />
                )}
        </DrillDownDrawer>
    );
}

function PartnersTab({ data, loading, error, onRetry, periodMeta }) {
    const partners = data?.partners?.items || [];
    const partialErrors = data?.partialErrors || [];

    const partnerMetricGroups = useMemo(() => {
        const waiting = { tag: "待接入", value: "--" };
        const avgCompletion = avgBy(partners, "completion_rate");
        return [
            {
                title: "服务请求",
                items: [
                    { label: "对象数", value: partners.length ? `${formatNumber(partners.length)} 个` : "--" },
                    { label: "总请求数", value: partners.length ? formatNumber(sumBy(partners, "total_request_count")) : "--" },
                    { label: "总完成数", value: partners.length ? formatNumber(sumBy(partners, "completed_request_count")) : "--" },
                    { label: "总待处理", value: partners.length ? formatNumber(sumBy(partners, "pending_request_count")) : "--" },
                    { label: "总异常", value: partners.length ? formatNumber(sumBy(partners, "exception_request_count")) : "--" },
                    { label: "平均完成率", value: isNil(avgCompletion) ? "--" : formatPercent(avgCompletion) },
                ],
            },
            {
                title: "航班与可靠性",
                items: [
                    { label: "执行航班数", ...waiting },
                    { label: "航班完成率", ...waiting },
                    { label: "技术可靠率", ...waiting },
                    { label: "SLA 达成率", ...waiting },
                ],
            },
            {
                title: "维修与 AOG",
                items: [
                    { label: "AOG 次数", ...waiting },
                    { label: "AOG 时长", ...waiting },
                    { label: "维修计划执行率", ...waiting },
                ],
            },
        ];
    }, [partners]);

    const columns = [
        { title: "对象", dataIndex: "partner_name", key: "partner_name", render: (value, record) => value || record.partner_id },
        { title: "服务类型", dataIndex: "service_type", key: "service_type", render: (value) => <Tag>{value}</Tag> },
        { title: "请求数", dataIndex: "total_request_count", key: "total_request_count", render: formatNumber },
        { title: "完成数", dataIndex: "completed_request_count", key: "completed_request_count", render: formatNumber },
        { title: "待处理", dataIndex: "pending_request_count", key: "pending_request_count", render: formatNumber },
        { title: "异常", dataIndex: "exception_request_count", key: "exception_request_count", render: formatNumber },
        { title: "完成率", dataIndex: "completion_rate", key: "completion_rate", render: (value) => <RateTag value={value} green={0.95} amber={0.9} /> },
        { title: "最近活动", dataIndex: "latest_activity_at", key: "latest_activity_at", render: formatDateTime },
        { title: "质量", dataIndex: "data_quality_level", key: "data_quality_level", render: (value) => <Tag color={qualityColor(value)}>{value}</Tag> },
    ];

    return (
        <div>
            <BlockError error={error} onRetry={onRetry} />
            <PartialRequestAlert partialErrors={partialErrors} message="部分托管航司与供应商数据加载失败" />
            <Spin spinning={loading}>
                <PanelCard title="托管航司与供应商汇总" extra={<Space size={6}><Tag color="blue">{periodMeta.label}口径</Tag><Text type="secondary">规划指标位 · 缺数据源处占位 --</Text></Space>}>
                    <MetricSlotGrid groups={partnerMetricGroups} />
                </PanelCard>
                <PanelCard title="托管航司与供应商表现">
                    <Table rowKey={(record) => `${record.partner_id}-${record.service_type}`} size="small" columns={columns} dataSource={partners} pagination={{ pageSize: 10 }} scroll={{ x: 1100 }} locale={{ emptyText: tableEmptyText("当前筛选条件下暂无托管航司或供应商数据") }} />
                </PanelCard>
            </Spin>
        </div>
    );
}

const tabItems = [
    { key: TAB_KEYS.OVERVIEW, label: "管理层总览" },
    { key: TAB_KEYS.AIRCRAFT, label: "飞机资产" },
    { key: TAB_KEYS.ROUTES, label: "航线经营" },
    { key: TAB_KEYS.CUSTOMERS, label: "客户履约" },
    { key: TAB_KEYS.PARTNERS, label: "托管航司与供应商" },
];

const BusinessDashboardPage = () => {
    const { hasPermission } = useAuth();
    const hasAmountPermission = hasPermission(PERMISSION_AMOUNT);
    const [filters, setFilters] = useState(defaultFilters);
    const [activeTab, setActiveTab] = useState(TAB_KEYS.OVERVIEW);
    const periodMeta = resolvePeriodMeta(filters.period);
    const { sharedData, tabData, loading, errors, loadShared, loadTab, reloadAll, requestParams } = useBusinessDashboardData({
        filters,
        hasAmountPermission,
    });

    useEffect(() => {
        loadShared().catch(() => undefined);
    }, [loadShared]);

    useEffect(() => {
        if (activeTab !== TAB_KEYS.OVERVIEW) {
            loadTab(activeTab).catch(() => undefined);
        }
    }, [activeTab, loadTab]);

    const handlePeriodChange = (period) => {
        const now = dayjs();
        const nextRange = period === "annual" ? annualRange(now) : monthlyRange(now);
        setFilters((prev) => ({
            ...prev,
            period,
            start_date: nextRange[0].format("YYYY-MM-DD"),
            end_date: nextRange[1].format("YYYY-MM-DD"),
        }));
    };

    const handleDateRangeChange = (dates) => {
        if (!dates || dates.length !== 2) return;
        setFilters((prev) => ({
            ...prev,
            start_date: dates[0].format("YYYY-MM-DD"),
            end_date: dates[1].format("YYYY-MM-DD"),
        }));
    };

    const handleReload = useCallback(() => {
        reloadAll(activeTab)
            .then(() => message.success("经营看板主要数据已重新加载，扩展区块继续更新"))
            .catch(() => message.error("经营看板数据重新加载失败"));
    }, [activeTab, reloadAll]);

    const activeContent = useMemo(() => {
        if (activeTab === TAB_KEYS.OVERVIEW) {
            return (
                <OverviewTab
                    sharedData={sharedData}
                    loading={loading.shared}
                    deferredLoading={loading.sharedDeferred}
                    error={errors.shared}
                    deferredError={errors.sharedDeferred}
                    onRetry={loadShared}
                    hasAmountPermission={hasAmountPermission}
                    periodMeta={periodMeta}
                />
            );
        }
        if (activeTab === TAB_KEYS.AIRCRAFT) {
            return <AircraftTab data={tabData.aircraft} loading={loading.aircraft} error={errors.aircraft} onRetry={() => loadTab(TAB_KEYS.AIRCRAFT, { force: true })} periodMeta={periodMeta} />;
        }
        if (activeTab === TAB_KEYS.ROUTES) {
            return <RoutesTab data={tabData.routes} loading={loading.routes} error={errors.routes} onRetry={() => loadTab(TAB_KEYS.ROUTES, { force: true })} periodMeta={periodMeta} />;
        }
        if (activeTab === TAB_KEYS.CUSTOMERS) {
            return <CustomersTab data={tabData.customers} loading={loading.customers} error={errors.customers} onRetry={() => loadTab(TAB_KEYS.CUSTOMERS, { force: true })} requestParams={requestParams} periodMeta={periodMeta} salesSummary={sharedData.salesSummary} hasAmountPermission={hasAmountPermission} />;
        }
        return <PartnersTab data={tabData.partners} loading={loading.partners} error={errors.partners} onRetry={() => loadTab(TAB_KEYS.PARTNERS, { force: true })} periodMeta={periodMeta} />;
    }, [activeTab, errors, hasAmountPermission, loadShared, loadTab, loading, periodMeta, requestParams, sharedData, tabData]);

    const rangeValue = [dayjs(filters.start_date), dayjs(filters.end_date)];

    return (
        <ConfigProvider theme={DASHBOARD_THEME}>
        <div className="page-container business-dashboard-page">
            <div className="business-dashboard-header">
                <div>
                    <Title level={3}>经营看板</Title>
                    <Text type="secondary">{`当前为${periodMeta.label}视图 · 基于排班、运控、结算和销售数据的管理层经营视图`}</Text>
                </div>
                <Space wrap>
                    <Segmented
                        value={filters.period}
                        onChange={handlePeriodChange}
                        options={[
                            { label: "月度", value: "monthly" },
                            { label: "年度", value: "annual" },
                        ]}
                    />
                    <RangePicker value={rangeValue} onChange={handleDateRangeChange} allowClear={false} />
                    <Tooltip title="同一筛选条件下页面会复用 5 分钟内缓存；点击后绕过缓存重新请求接口，不提交后台刷新任务">
                        <Button type="primary" icon={<ReloadOutlined />} loading={loading.shared || loading[activeTab]} onClick={handleReload}>
                            重新加载
                        </Button>
                    </Tooltip>
                </Space>
            </div>

            <KpiStrip overview={sharedData.overview} bhSummary={sharedData.bhSummary} bhTimeSeries={sharedData.bhTimeSeries} loading={loading.shared} periodMeta={periodMeta} />

            {!hasAmountPermission ? (
                <Alert
                    className="business-dashboard-permission-alert"
                    type="info"
                    showIcon
                    message="金额区块已按权限隐藏"
                    description="当前账号未请求销售金额接口，收入、到账、燃油附加费等敏感金额不会在页面中展示。"
                />
            ) : null}

            <Card className="business-dashboard-tabs-card">
                <Tabs
                    activeKey={activeTab}
                    onChange={setActiveTab}
                    items={tabItems.map((item) => ({
                        ...item,
                        children: activeTab === item.key ? activeContent : null,
                    }))}
                />
            </Card>
        </div>
        </ConfigProvider>
    );
};

export default BusinessDashboardPage;
